/**
 * A wrapper for better Select lists
 */
namespace appn.select {

    class Args {
        selector?: string = emptyString;
        minimumResultsForSearch?: string = emptyString;
        isIconSelect?: boolean = false;
        isFASelect?: boolean = false;
        isIconButtonSelect?: boolean = false;
        isIconTextSelect?: boolean = false;
        isHierarchical?: boolean = false;
        omitWidth?: boolean = false;
        onSelectCallback?: Function;
        onOpenCallback?: Function;
    }

    let _initArgs: Args;

    interface IHierarchyNode {
        value: string;
        parentValue: string | null;
        depth: number;
        hasChildren: boolean;
        children: string[];
    }

    const _hierarchyData = new Map<string, IHierarchyNode>();
    const _expandedNodes = new Set<string>();
    let _hierarchicalToggleHandler: ((e: Event) => void) | null = null;

    function handleHierarchicalToggleEvent(e: Event): void {
        const target = e.target as HTMLElement;
        const toggle = target.closest('.select2-dropdown .expand-toggle');
        if (!toggle) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const $toggle = $(toggle);
        const $option = $toggle.closest('.select2-results__option');
        const $hierarchical = $option.find('.hierarchical-option');
        const value = $hierarchical.data('value');

        if (!value) return;

        const node = _hierarchyData.get(value);
        if (!node || !node.hasChildren) return;

        if (_expandedNodes.has(value)) {
            collapseNode(value);
            $toggle.removeClass('expanded');
        } else {
            expandNode(value);
            $toggle.addClass('expanded');
        }

        updateHierarchicalVisibility();
    }

    function buildHierarchyData($select: JQuery): void {
        _hierarchyData.clear();
        _expandedNodes.clear();

        const options = $select.find('option').toArray();
        const valueToOption = new Map<string, HTMLElement>();

        options.forEach(opt => {
            const $opt = $(opt);
            const value = $opt.val() as string;
            if (value) {
                valueToOption.set(value, opt);
            }
        });

        options.forEach(opt => {
            const $opt = $(opt);
            const value = $opt.val() as string;
            if (!value) return;

            const parentValue = $opt.data('parent-value') as string | undefined;

            let depth = 0;
            let currentParent = parentValue;
            const visited = new Set<string>();
            while (currentParent && !visited.has(currentParent)) {
                visited.add(currentParent);
                depth++;
                const parentOpt = valueToOption.get(currentParent);
                if (parentOpt) {
                    currentParent = $(parentOpt).data('parent-value') as string | undefined;
                } else {
                    break;
                }
            }

            _hierarchyData.set(value, {
                value: value,
                parentValue: parentValue || null,
                depth: depth,
                hasChildren: false,
                children: []
            });
        });

        _hierarchyData.forEach((node, value) => {
            if (node.parentValue) {
                const parentNode = _hierarchyData.get(node.parentValue);
                if (parentNode) {
                    parentNode.hasChildren = true;
                    parentNode.children.push(value);
                }
            }
        });
    }

    function isNodeVisible(value: string): boolean {
        const node = _hierarchyData.get(value);
        if (!node) return true;

        if (node.depth === 0) return true;

        let currentValue: string | null = node.parentValue;
        while (currentValue) {
            if (!_expandedNodes.has(currentValue)) return false;
            const parentNode = _hierarchyData.get(currentValue);
            currentValue = parentNode?.parentValue || null;
        }

        return true;
    }

    function getOptionElementByValue(value: string): JQuery {
        return $(`.select2-results__option`).filter(function() {
            return $(this).find('.hierarchical-option').data('value') === value;
        });
    }

    function expandNode(value: string): void {
        const node = _hierarchyData.get(value);
        if (!node || !node.hasChildren) return;

        _expandedNodes.add(value);

        node.children.forEach(childValue => {
            const $childOption = getOptionElementByValue(childValue);
            $childOption.removeClass('hierarchical-hidden');
        });
    }

    function collapseNode(value: string): void {
        const node = _hierarchyData.get(value);
        if (!node || !node.hasChildren) return;

        _expandedNodes.delete(value);

        function hideDescendants(parentValue: string): void {
            const parentNode = _hierarchyData.get(parentValue);
            if (!parentNode) return;

            parentNode.children.forEach(childValue => {
                const $childOption = getOptionElementByValue(childValue);
                $childOption.addClass('hierarchical-hidden');
                hideDescendants(childValue);
            });
        }

        hideDescendants(value);
    }

    function expandAncestorsOfMatch(value: string): void {
        const node = _hierarchyData.get(value);
        if (!node) return;

        let currentValue: string | null = node.parentValue;
        while (currentValue) {
            _expandedNodes.add(currentValue);
            currentValue = _hierarchyData.get(currentValue)?.parentValue || null;
        }
    }

    export function init(initArgs?: Args) {
        $(document).on('select2:open', (event) => {
           if (!(event.target as any).multiple)
               window.setTimeout(() => $('.select2-container--open .select2-search--dropdown .select2-search__field').last()[0].focus(), 0);
        });

        // Select2 left-aligns dropdowns with their trigger, so flyouts on right-docked selects
        // can overflow the viewport. Align the right edge of an overflowing dropdown with the
        // right edge of its selection container instead. Reposition on the next animation frame
        // so the corrected position is applied before the first paint (avoids a visible jump).
        $(document).on('select2:open', (event) => {
            const target = event.target as any;
            const select2Data = $(target).data('select2') as any;
            if (!select2Data || !select2Data.$dropdown)
                return;

            window.requestAnimationFrame(() => {
                const containerRect = (target.nextElementSibling as HTMLElement)?.getBoundingClientRect();
                const dropdownWidth = (select2Data.$dropdown as JQuery).outerWidth() || 0;
                const winWidth = $(window).width() || 0;
                if (containerRect && dropdownWidth && containerRect.left + dropdownWidth > winWidth)
                    (select2Data.$dropdown as JQuery).css('left', Math.max(containerRect.right - dropdownWidth, 0) + 'px');
            });
        });

        if (!initArgs)
            initArgs = new Args();

        _initArgs = initArgs;

        if (!initArgs.hasOwnProperty('selector') || !initArgs.selector)
            initArgs.selector = 'select:not(.bootstrap-duallistbox-container select,select.multiselect,#iconControlTemplate .select-icons,.select2-noinit,select#iconsToDisplay)';

        if (!initArgs.hasOwnProperty('minimumResultsForSearch') || !initArgs.minimumResultsForSearch)
            initArgs.minimumResultsForSearch = '50';

        if (!initArgs.hasOwnProperty('omitWidth') || !initArgs.omitWidth)
            initArgs.omitWidth = false;

        const select2Options: any = {
            theme: 'bootstrap4',
            minimumResultsForSearch: initArgs.minimumResultsForSearch,
            width: initArgs.omitWidth ? '' : '100%',
            dropdownAutoWidth: true,
            dropdownCssClass: ":all:"       // copy all CSS classes from container to the rendered dropdown (helps carry -sm etc to dropdowns)
        };

        if (initArgs.hasOwnProperty('isIconSelect') && initArgs.isIconSelect) {
            select2Options.templateResult = formatStateIcons;
            select2Options.templateSelection = formatStateIcons;
            select2Options.minimumResultsForSearch = '200';
        }
        else if (initArgs.hasOwnProperty('isFASelect') && initArgs.isFASelect) {
            select2Options.templateResult = formatStateFAResult;
            select2Options.templateSelection = formatStateFA;
            select2Options.minimumResultsForSearch = '200';
            select2Options.escapeMarkup = function(m: any) {
                return m;
            };
            select2Options.width = '50px';
        }
        else if (initArgs.hasOwnProperty('isIconButtonSelect') && initArgs.isIconButtonSelect) {
            select2Options.templateResult = formatStateIconButtonResult;
            select2Options.templateSelection = formatStateIconButtonSelection;
            select2Options.minimumResultsForSearch = 'Infinity';
            select2Options.width = 'auto';
            select2Options.escapeMarkup = function(m: any) {
                return m;
            };
        }
        else if (initArgs.hasOwnProperty('isIconTextSelect') && initArgs.isIconTextSelect) {
            select2Options.templateResult = formatStateIconText;
            select2Options.templateSelection = formatStateIconText;
        }

        if ($(initArgs.selector as string).find('option').filter('[data-title]').length > 0) {
            select2Options.templateResult = formatStateOptionTitles;
            select2Options.templateSelection = formatStateOptionTitles;

            $(document).on('mouseenter', '.select2-results__option', function () {
                $('.select2-results__option span[title]').smallipop({ preferredPosition: 'right', theme: 'black', popupOffset: 100 });
                $('li.select2-results__option').removeAttr('data-original-title').removeAttr('title');
            });
            
            $(document).on('select2:close select2:select', initArgs.selector as string, function (e) {
                // Force hide on select or close
                $('.smallipop-instance').hide();
            });
        }

        $(initArgs.selector).each((index: number, select: HTMLElement) => {
            if ($(select).attr('multiple'))
                removeDefaultFromMultiselect($(select));
        });

        ($(initArgs.selector) as any).select2(select2Options);

        copyOriginalSelectsAttrs(initArgs);

        // Add a main level class if this is for icons
        if (initArgs.isIconSelect) {
            const select2 = $(initArgs.selector as string).next('.select2');
            select2.removeClass('select2-appnIcons').addClass('select2-appnIcons');
        }

        if (initArgs.isIconButtonSelect) {
            $(initArgs.selector as string).each(function() {
                const select2 = $(this).next('.select2');
                select2.addClass('select2-iconButton');
            });
        }

        if (initArgs.hasOwnProperty('onSelectCallback') && initArgs.onSelectCallback)
            $(initArgs.selector).on('select2:select', function(e: any) {
                initArgs!.onSelectCallback!(e);
            });

        if (initArgs.hasOwnProperty('onOpenCallback') && initArgs.onOpenCallback)
            $(initArgs.selector).on('select2:open', function(e: any) {
                setTimeout(function() {
                    initArgs!.onOpenCallback!(e);
                }, 250);
            });

        $(document).on('mouseenter', '.select2-selection__rendered', function() {
            $(this).removeAttr('data-original-title').removeAttr('title');
        });
    }

    export function initForDesign(initArgs: { elements: JQuery, omitWidth?: boolean, minimumResultsForSearch?: string, isIconButtonSelect?: boolean, isIconTextSelect?: boolean, isHierarchical?: boolean }) {
        const select2Options: any = {
            theme: 'bootstrap4',
            minimumResultsForSearch: initArgs.minimumResultsForSearch || '50',
            width: initArgs.omitWidth ? '' : '100%',
            dropdownAutoWidth: true,
            dropdownCssClass: ":all:"
        };

        if (initArgs.elements.find('option').filter('[data-title]').length > 0) {
            select2Options.templateResult = formatStateOptionTitles;
            select2Options.templateSelection = formatStateOptionTitles;
        }

        if (initArgs.isIconButtonSelect) {
            select2Options.templateResult = formatStateIconButtonResult;
            select2Options.templateSelection = formatStateIconButtonSelection;
            select2Options.minimumResultsForSearch = 'Infinity';
            select2Options.width = 'auto';
            select2Options.escapeMarkup = function(m: any) {
                return m;
            };
        }

        if (initArgs.isIconTextSelect) {
            select2Options.templateResult = formatStateIconText;
            select2Options.templateSelection = formatStateIconText;
        }

        if (initArgs.isHierarchical) {
            select2Options.templateResult = formatStateHierarchical;
            select2Options.matcher = hierarchicalMatcher;
        }

        initArgs.elements.each((index: number, select: HTMLElement) => {
            const $select = $(select);
            if ($select.hasClass('select2-hidden-accessible')) {
                return; // Already initialized, skip.
            }

            if ($select.attr('multiple')) {
                removeDefaultFromMultiselect($select);
            }

            if (initArgs.isHierarchical) {
                buildHierarchyData($select);
            }

            ($select as any).select2(select2Options);

            if (initArgs.isHierarchical) {
                bindHierarchicalEvents($select);
            }

            const newSelect2 = $select.next('.select2');
            if (!newSelect2 || !newSelect2.length) {
                return;
            }

            // Move input-sm
            if ($select.hasClass('form-control-sm'))
                newSelect2.addClass('select2-sm');

            // Add icon button class
            if (initArgs.isIconButtonSelect)
                newSelect2.addClass('select2-iconButton');

            // Move id
            if ($select.prop('id')) {
                const newId = `${$select.prop('id')}-select2`;
                newSelect2.prop('id', newId);
            }

            // Move float-right
            if ($select.hasClass('float-right'))
                newSelect2.addClass('float-right');

            // Move float-left
            if ($select.hasClass('float-left'))
                newSelect2.addClass('float-left');

            // Move hidden
            if ($select.hasClass('d-none'))
                newSelect2.addClass('d-none');
        });
    }

    export function destroy(selector: string) {
        if ($(selector).attr('data-select2-id')) {
            ($(selector) as any).select2('destroy');
            $(selector).removeAttr('data-select2-id');
        }
    }

    /**
     * Mutates the current state of a dropdown option for icons to display the source image
     *  instead of the name of the icon
     */
    function formatStateIcons(state: any) {
        if (!state.id)
            return state.text;

        const $state = $(`<img src="${state.element.dataset.imagesrc}" class="img-select2" />`);
        if (_initArgs.hasOwnProperty('isIconSelect') && _initArgs.isIconSelect)
            $state.append($(`<span class="img-select2-text">${state.element.value}</span>`));

        return $state;
    }

    /**
     * Mutates the current state of a dropdown option for icons to display the source font awesome icon
     */
    function formatStateFA(icon: any) {
        if (!icon.id)
            return icon.text;

        const iconEl = elementBuilder.createElement('i', ['fa', $(icon.element).attr('class') as string, 'fa-lg', 'fa-fw'], undefined, [['style', $(icon.element).attr('style') as string]]);
        if ($(icon.element).attr('data-parent'))
            $(iconEl).attr('data-parent', $(icon.element).attr('data-parent') as string);
        const span = elementBuilder.createElement('span');
        span.append(iconEl);

        return $(span);
    }

    /**
     * Mutates the dropdown option for font awesome icon selects to display the icon with its text label
     */
    function formatStateFAResult(icon: any) {
        if (!icon.id)
            return icon.text;

        const iconEl = elementBuilder.createElement('i', ['fa', $(icon.element).attr('class') as string, 'fa-lg', 'fa-fw'], undefined, [['style', $(icon.element).attr('style') as string]]);
        if ($(icon.element).attr('data-parent'))
            $(iconEl).attr('data-parent', $(icon.element).attr('data-parent') as string);
        const span = elementBuilder.createElement('span', 'select2-fa_result');
        span.append(iconEl);
        span.append(` ${icon.text}`);

        return $(span);
    }

    /**
     * Mutates the dropdown option for icon button selects to display icon + text
     */
    function formatStateIconButtonResult(state: any): string | JQuery {
        if (!state.id)
            return state.text;

        const icon = $(state.element).data('icon');
        const $state = $(`<span><i class="fa ${icon}"></i> ${state.text}</span>`);
        applyOptionContainerClass($state, state);
        return $state;
    }

    /**
     * Mutates the selection for icon button selects to display only the icon
     */
    function formatStateIconButtonSelection(state: any): string | JQuery {
        if (!state.id)
            return state.text;

        const icon = $(state.element).data('icon');
        return $(`<i class="fa ${icon}"></i>`);
    }

    /**
     * Builds an option's leading icon: a glyph when the option carries data-icon-class (e.g. the lock for a
     * restricted dataset), otherwise the image from data-icon-url with its optional data-icon-tooltip
     */
    function buildOptionIcon(state: any): JQuery | null {
        const iconClass = $(state.element).data('icon-class');
        if (iconClass)
            return $(`<i class="${iconClass} select2-option-icon"></i>`);

        const iconUrl = $(state.element).data('icon-url');
        if (!iconUrl)
            return null;

        return $('<img>', {
            src: iconUrl,
            class: 'select2-option-icon',
            title: $(state.element).data('icon-tooltip') || ''
        });
    }

    /**
     * Mutates dropdown option and selection to display icon + text with tooltip
     * Uses the data-icon-class / data-icon-url and data-icon-tooltip attributes on the option element
     */
    function formatStateIconText(state: any): string | JQuery {
        if (!state.id)
            return state.text;

        const $icon = buildOptionIcon(state);
        if (!$icon)
            return state.text;

        const $state = $('<span>').append($icon).append(` ${state.text}`);
        applyOptionContainerClass($state, state);
        return $state;
    }

    /**
     * Copies an option's data-container-class onto the rendered container so callers can style it
     * (e.g. a read-only restricted dataset option)
     */
    function applyOptionContainerClass($container: JQuery, state: any): void {
        const containerClass = $(state.element).data('container-class');
        if (containerClass)
            $container.addClass(containerClass);
    }

    /**
     * Formats hierarchical options with expand/collapse toggle and depth-based styling
     */
    function formatStateHierarchical(state: any): JQuery | string {
        if (!state.id)
            return state.text;

        const value = state.id;
        const node = _hierarchyData.get(value);
        const depth = node?.depth || 0;
        const hasChildren = node?.hasChildren || false;
        const isExpanded = _expandedNodes.has(value);

        const $container = $('<span>', {
            class: `hierarchical-option depth-${depth}`,
            'data-value': value,
            'data-depth': depth
        });

        // Let an option pass extra container classes (e.g. to flag a read-only restricted dataset)
        applyOptionContainerClass($container, state);

        const $icon = buildOptionIcon(state);
        if ($icon)
            $container.append($icon);

        $container.append(`<span class="option-text">${state.text}</span>`);

        if (hasChildren) {
            const $toggle = $('<span>', {
                class: `expand-toggle ${isExpanded ? 'expanded' : ''}`,
                html: '<i class="fa fa-chevron-right"></i>'
            });
            $container.append($toggle);
        }

        return $container;
    }

    /**
     * Custom matcher for hierarchical selects that searches across all options
     * and auto-expands ancestor paths for matches
     */
    function hierarchicalMatcher(params: any, data: any): any {
        if ($.trim(params.term) === '') {
            return data;
        }

        if (!data.id) {
            return null;
        }

        const text = data.text.toUpperCase();
        const term = params.term.toUpperCase();

        if (text.indexOf(term) > -1) {
            expandAncestorsOfMatch(data.id);
            return data;
        }

        return null;
    }

    /**
     * Binds event handlers for hierarchical expand/collapse
     */
    function bindHierarchicalEvents($select: JQuery): void {
        $select.on('select2:open', function() {
            setTimeout(function() {
                autoExpandSelected($select);
                updateHierarchicalVisibility();
                updateToggleStates();
            }, 10);
        });

        $select.on('select2:closing', function() {
            _expandedNodes.clear();
        });

        // Use capture phase to intercept mouseup before select2 processes it
        // Select2 uses 'mouseup' for selection (line 1285 in select2 source)
        if (!_hierarchicalToggleHandler) {
            _hierarchicalToggleHandler = handleHierarchicalToggleEvent;
            document.addEventListener('mouseup', _hierarchicalToggleHandler, true);
        }
    }

    /**
     * Auto-expands ancestors of currently selected value
     */
    function autoExpandSelected($select: JQuery): void {
        const selectedValue = $select.val() as string;
        if (!selectedValue) return;

        const node = _hierarchyData.get(selectedValue);
        if (!node || node.depth === 0) return;

        expandAncestorsOfMatch(selectedValue);
    }

    /**
     * Updates visibility of hierarchical options based on expanded state
     */
    function updateHierarchicalVisibility(): void {
        _hierarchyData.forEach((node, value) => {
            const $option = getOptionElementByValue(value);
            if ($option.length) {
                if (isNodeVisible(value)) {
                    $option.removeClass('hierarchical-hidden');
                } else {
                    $option.addClass('hierarchical-hidden');
                }
            }
        });
    }

    /**
     * Updates toggle icon expanded state to match _expandedNodes
     */
    function updateToggleStates(): void {
        _hierarchyData.forEach((node, value) => {
            if (node.hasChildren) {
                const $toggle = $('.select2-dropdown .expand-toggle').filter(function() {
                    return $(this).closest('.hierarchical-option').data('value') === value;
                });
                if (_expandedNodes.has(value)) {
                    $toggle.addClass('expanded');
                } else {
                    $toggle.removeClass('expanded');
                }
            }
        });
    }

    /**
     * Mutates the current state of a dropdown option for titles to be populated 
     *  and allowing smallipop to initialise for styled pop-ups on hover
     */
    function formatStateOptionTitles(state: any) {
        if (!state.id)
            return state.text;

        if (state.element.dataset.title)
            state.title = state.element.dataset.title;

        const $state = $(elementBuilder.createElement('span', 'select2-span_result', undefined, [['title', state.element.dataset.title], ['value', state.id]], state.element.text));
        return $state;
    }

    /**
     * Mutates the original select removing options without value attributes
     *  to ensure that multiselect dropdowns don't have default tags with no value
     */
    function removeDefaultFromMultiselect(select: JQuery<HTMLElement>): void {
        select.find('option').each((index: number, option: HTMLElement) => {
            if (!$(option).attr('value'))
                select.find(option).eq(index).remove();
        });
    }

    /**
     * Mutate the NEW Select2 dropdown to inherit original select classes where applicable
     */
    function copyOriginalSelectsAttrs(initArgs: Args) {
        // Auto apply small select box styling if the parent native select has input-sm
        $(initArgs.selector as string).each(function(): any {
            const newSelect2 = $(this).next('.select2');

            if (!newSelect2 || !newSelect2.length)
                return true;

            // Move input-sm
            if ($(this).hasClass('form-control-sm'))
                newSelect2.removeClass('select2-sm').addClass('select2-sm');

            // Move id
            if ($(this).prop('id')) {
                const newId = `${$(this).prop('id')}-select2`;
                newSelect2.prop('id', newId);
            }

            // Move pull-right
            if ($(this).hasClass('float-right'))
                newSelect2.removeClass('float-right').addClass('float-right');

            // Move pull-left
            if ($(this).hasClass('float-left'))
                newSelect2.removeClass('float-left').addClass('float-left');

            // Move hidden
            if ($(this).hasClass('d-none'))
                newSelect2.removeClass('d-none').addClass('d-none');
        });
    }
}
